import useConfig from 'modules/config/state/use-config'
import OrderContactQuestionField from './contact-question-field'
// TODO: Re-enable when custom address questions are available again
// import AddressQuestionField from './address-question-field'
import Row from 'modules/ui/atoms/Layout/Row'
import { ADDRESS_TAGS } from 'modules/config/utils/address-questions-utils'
import { OrderContactAnswersSlice } from 'modules/order/state/contact-answers/contact-answers-types'

interface OrderContactQuestions2Props {
  showRequired: boolean
  showNameFields?: boolean // New prop to control if prefix, middle_name, suffix should be shown
  useGridLayout?: boolean // New prop to control two-column layout
}

const OrderContactQuestions2 = ({
  showRequired,
  showNameFields = false,
  useGridLayout = false,
}: OrderContactQuestions2Props) => {
  const config = useConfig()

  // Filter to non-address questions only
  const filteredQuestions = config.questions.contact.filter((q) => {
    if (!q.tag) return false

    // Exclude address questions (they'll be handled separately)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (ADDRESS_TAGS.includes(q.tag as any)) return false

    // If showNameFields is true, show all fields including prefix, middle_name, suffix
    // If showNameFields is false, filter out prefix, middle_name, suffix (old behavior)
    const nameFields = ['prefix', 'middle_name', 'suffix']

    return showNameFields || nameFields.indexOf(q.tag) === -1
  })

  // Define field pairs for grid layout (which fields should be on the same row)
  const fieldPairs = [
    ['middle_name'],
    ['suffix', 'prefix'], // Suffix and Title on same row
    ['cellphone'],
    ['birthdate'],
    ['company_name', 'website'], // Company and Website on same row
    ['gender', 'pronoun'], // Gender and Pronouns on same row
  ]

  type ContactQuestion = (typeof filteredQuestions)[number]

  const renderField = (question: ContactQuestion) => {
    const key =
      'id' in question && question.id ? String(question.id as number) : (question.tag as string)

    return (
      <OrderContactQuestionField
        key={key}
        showRequired={showRequired}
        fieldName={question.tag as keyof OrderContactAnswersSlice}
      />
    )
  }

  if (!useGridLayout) {
    return (
      <>
        {/* TODO: Re-enable address questions when custom address questions are available again */}
        {/* {addressQuestions.length > 0 && (
          <AddressQuestionField addressQuestions={addressQuestions} showRequired={showRequired} />
        )} */}
        {filteredQuestions.map((question) => renderField(question))}
      </>
    )
  }

  // Grid layout: group fields into rows
  const renderedFields = new Set<string>()
  const rows: JSX.Element[] = []

  fieldPairs.forEach((pairTags, pairIndex) => {
    const fieldsInRow = pairTags
      .map((tag) => filteredQuestions.find((q) => q.tag === tag))
      .filter(
        (q): q is NonNullable<typeof q> =>
          q !== undefined && q.tag !== undefined && !renderedFields.has(q.tag),
      )

    if (fieldsInRow.length > 0) {
      // Mark these fields as rendered
      fieldsInRow.forEach((field) => {
        if (field.tag) {
          renderedFields.add(field.tag)
        }
      })

      if (fieldsInRow.length === 1) {
        // Single field gets full width
        rows.push(<div key={`row-${pairIndex}`}>{renderField(fieldsInRow[0])}</div>)
      } else {
        // Multiple fields in a row
        rows.push(
          <Row key={`row-${pairIndex}`}>{fieldsInRow.map((field) => renderField(field))}</Row>,
        )
      }
    }
  })

  // Render any remaining fields that weren't in the predefined pairs
  const remainingFields = filteredQuestions.filter((q) => q.tag && !renderedFields.has(q.tag))

  remainingFields.forEach((field, index) => {
    rows.push(<div key={`remaining-${index}`}>{renderField(field)}</div>)
  })

  return (
    <>
      {/* TODO: Re-enable address questions when custom address questions are available again */}
      {/* {addressQuestions.length > 0 && (
        <AddressQuestionField addressQuestions={addressQuestions} showRequired={showRequired} />
      )} */}
      {rows}
    </>
  )
}

export default OrderContactQuestions2
